import React, { useState, useRef, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { ProjectCardMenu, MenuAction } from '@shared/components/ProjectCardMenu/ProjectCardMenu';
import { ProjectCardLabels, Label } from '@shared/components/ProjectCardLabels/ProjectCardLabels';
import {
  formatRelativeTime,
  formatLocation,
  BaseProjectProps,
  defaultProjectTranslations,
  getAvailableProjectMenuActions
} from './utils';
import userImage from '@shared/assets/icon-user.svg';
import teamImage from '@shared/assets/icon-team.svg';
import defaultProjectImage from '@shared/assets/Logo-symbol-dark.svg';
import styles from './ProjectCard.module.css';

// Re-export types for convenience
export type { Label };
export type { MenuAction };

/**
 * ProjectCard - Vertical card component for displaying project information
 * Fixed dimensions: 295x350px with image, title, details, labels, and menu
 */
export const ProjectCard: React.FC<BaseProjectProps> = ({
  user,
  id,
  name,
  client,
  size,
  labels = [],
  location,
  updatedAt,
  members,
  type = 0,
  image,
  onClick,
  menuActions,
  onMenuAction,
  translations,
  notEntered = ''
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Merge provided translations with defaults
  const t = { ...defaultProjectTranslations, ...translations };

  // Set default menu actions using translations
  // Only show upgrade option for private projects (type === 0)
  const defaultMenuActions: MenuAction[] = [
    { id: 'edit', label: t.menuEdit!, icon: 'edit' },
    { id: 'delete', label: t.menuDelete!, icon: 'delete' },
    ...(type === 0 ? [{ id: 'upgrade', label: t.menuUpgrade!, icon: 'upgrade' }] : [])
  ];

  const availableMenuActions = getAvailableProjectMenuActions(
    menuActions || defaultMenuActions,
    members,
    user
  );

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleMenuAction = (actionId: string) => {
    setIsMenuOpen(false);
    onMenuAction?.(id, actionId);
  };

  // Sort labels alphabetically
  const sortedLabels = useMemo(() => {
    return [...labels].sort((a, b) => {
      // Handle both string labels and object labels with .label property
      const labelA = (typeof a === 'string' ? a : a?.label || '').trim().toLowerCase();
      const labelB = (typeof b === 'string' ? b : b?.label || '').trim().toLowerCase();
      if (labelA < labelB) return -1;
      if (labelA > labelB) return 1;
      return 0;
    });
  }, [labels]);

  return (
    <div
      data-test="project-card"
      className={clsx(styles.card, { [styles.cardClickable]: !!onClick })}
      onClick={onClick}
    >
      {/* Menu Button */}
      <div ref={menuRef} className={styles.menuContainer}>
        {!isMenuOpen && (
          <button
            data-test="project-card-menu-button"
            className={styles.menuButton}
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(true);
            }}
            aria-label="More options"
          >
            <span className={clsx("material-icons", styles.menuIcon, styles.materialIcon)}>
              more_vert
            </span>
          </button>
        )}

        {/* Dropdown Menu */}
        <ProjectCardMenu
          isOpen={isMenuOpen}
          actions={availableMenuActions}
          onClose={() => setIsMenuOpen(false)}
          onActionClick={handleMenuAction}
        />
      </div>
      
      {/* CSS Animation */}
      <style>
        {`
          @keyframes fadeIn {
            0% {
              opacity: 0;
              transform: scale(0.5);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>

      {/* Card Content */}
      <div className={styles.content}>
        {/* Icon */}
        <div className={styles.iconContainer} data-test="project-card-image">
          <img 
            src={(image?.url && !imageError) ? image.url : defaultProjectImage} 
            alt={name}
            onError={() => setImageError(true)}
            className={clsx(styles.image, { [styles.imageDefault]: !image?.url || imageError })}
          />
        </div>

        {/* Project Title */}
        <div className={styles.titleContainer}>
          <img 
            src={!Boolean(type) ? userImage : teamImage}
            alt={!Boolean(type) ? 'Private project' : 'Team project'}
            className={styles.typeIcon}
          />
          <h3 className={styles.title} data-test="project-card-title" title={name}>{name}</h3>
        </div>

        {/* Project Details */}
        <div className={styles.details} data-test="project-card-details">
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>{t.clientLabel}:</span>
            <span className={clsx(styles.detailValue, { [styles.detailValueEmpty]: !client })}>
              {client || notEntered}
            </span>
          </div>
          
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>{t.sizeLabel}:</span>
            <span className={clsx(styles.detailValue, { [styles.detailValueEmpty]: !size })}>
              {size ? size + ' m2' : notEntered}
            </span>
          </div>
          
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>{t.locationLabel}:</span>
            <span className={clsx(styles.detailValue, { [styles.detailValueEmpty]: !formatLocation(location) })}>
              {formatLocation(location) || notEntered}
            </span>
          </div>
        </div>

        {/* Labels */}
        <ProjectCardLabels labels={sortedLabels} data-test="project-card-labels" />
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <span className={styles.footerText}>{formatRelativeTime(updatedAt, t.relativeTime)}</span>
      </div>
    </div>
  );
};

